import Button from '@/components/Button';
import ButtonSwitchInput from '@/components/form/ButtonSwitchInput';
import TextInput from '@/components/form/TextInput';
import Layout from '@/components/Layout';
import SEO from '@/components/SEO';
import { getPlantHardinessZone } from '@/lib/connectors/hardiness';
import { getViableTrees } from '@/lib/connectors/trees';
import { TerrainData, useTerrainData } from '@/lib/data';
import { useFormik } from 'formik';
import { navigate, PageProps } from 'gatsby';
import * as React from 'react';
import { FC, useMemo } from 'react';
import * as Yup from 'yup';

const Home: FC<PageProps> = () => {
  const terrainData = useTerrainData();

  const formik = useFormik({
    initialValues: {
      sun: terrainData.sun ?? ('' as TerrainData['sun']),
      minTemperature: terrainData.minTemperature ?? ('' as unknown as number),
      soilHumidity:
        terrainData.soilHumidity ?? ('' as TerrainData['soilHumidity']),
    },
    validationSchema: Yup.object({
      sun: Yup.string().required(),
      minTemperature: Yup.number().required(),
      soilHumidity: Yup.string().required(),
    }),
    onSubmit: async ({ sun, minTemperature, soilHumidity }) => {
      terrainData.setTerrainData({ sun, minTemperature, soilHumidity });
      await navigate('/tree-guilds/');
    },
  });

  const numberOfViableTrees = useMemo(() => {
    const { minTemperature, soilHumidity, sun } = formik.values as any;

    return minTemperature !== '' && soilHumidity !== '' && sun !== ''
      ? getViableTrees({
          hardiness: getPlantHardinessZone(minTemperature),
          sun,
          soilHumidity,
        }).length
      : null;
  }, [formik.values]);

  return (
    <Layout className="relative">
      <SEO url="" title="Freeden" description="Tree guild builder" />

      <div className="container m-auto">
        <h1 className="text-3xl text-center font-bold w-full my-10">Freeden</h1>
        <form
          className="shadow-lg rounded-md max-w-xl m-auto p-8 bg-white"
          onSubmit={formik.handleSubmit}
        >
          <h2 className="text-lg mb-10">
            Before we begin, tell us a little more{' '}
            <strong>about the terrain</strong> you wish to plant the tree
            guilds.
          </h2>

          <ButtonSwitchInput
            formik={formik}
            name="sun"
            label="How sunny is your terrain?"
            values={[
              { value: '+', label: '🌕 Full Sun' },
              { value: '/', label: '🌓 Partial Shade' },
              { value: '-', label: '🌑 Shade' },
            ]}
          />
          <div className="mb-8" />
          <TextInput
            formik={formik}
            name="minTemperature"
            label="What's the annual minimal temperature (°C)?"
            type="number"
            placeholder="eg: -10.4"
          />
          <div className="mb-8" />
          <ButtonSwitchInput
            formik={formik}
            name="soilHumidity"
            label="Is the terrain well drained or puddling?"
            values={[
              { value: 'D', label: '🌵 Well Drained' },
              { value: 'P', label: '💧 Puddling' },
            ]}
          />
          {numberOfViableTrees !== null && (
            <div className="text-sm text-gray-400 mt-16">
              We found <strong>{numberOfViableTrees || 'no'}</strong> trees that
              you could plan in these conditions.
            </div>
          )}
          <div className="mb-16" />
          <div className="w-full flex justify-end">
            <div>
              <Button
                type="submit"
                color="secondary"
                className="flex items-center"
              >
                Create Tree guild
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 ml-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default Home;
